/* AdminDataGrid — reusable admin table primitive.
 *
 * Built for the Retail Catalog UX sprint (Feb 2026) so a single
 * component owns the cross-cutting admin-page concerns:
 *
 *   - Sortable column headers (click-once-asc, click-twice-desc)
 *   - Sort indicators
 *   - Search input
 *   - Filter pills
 *   - Toggle-switch column type
 *   - Per-row action renderer
 *   - Server-side pagination footer (default 50/page)
 *   - Loading + empty states
 *   - Visible grid lines between rows and columns
 *
 * Future consumers: Customers, Leads, Retail Orders, Shipping,
 * Email Logs, Conversations, Analytics. Each just defines `columns`
 * and feeds the controlled state (page / sort / search / filter)
 * via the props below.
 *
 * Important: this component is INTENTIONALLY controlled — the parent
 * owns `page`, `sortBy`, `sortDir`, `search`, `activeFilter`. Server
 * pagination is the default; client-side paging is fine too (just feed
 * the slice yourself and keep `totalCount` accurate).
 */
import React from "react";
import { ChevronUp, ChevronDown, ChevronsUpDown, Search } from "lucide-react";

/* --------------------------------------------------------------------- *
 * Column toggle column helper
 * --------------------------------------------------------------------- */

export function ToggleCell({
  checked, onChange, busy, testId,
  labelOn = "Yes", labelOff = "No",
}) {
  // Tailwind-only iOS-style toggle — no shadcn dep so the component
  // can ship from /components/admin/ without dragging in extra UI.
  const onClick = () => { if (!busy && onChange) onChange(!checked); };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      role="switch"
      aria-checked={checked}
      data-testid={testId}
      data-state={checked ? "on" : "off"}
      className={
        "relative inline-flex h-5 w-9 items-center rounded-full transition-colors " +
        "focus:outline-none focus:ring-2 focus:ring-orange-400 focus:ring-offset-1 " +
        (checked
          ? "bg-emerald-500 hover:bg-emerald-600"
          : "bg-slate-300 hover:bg-slate-400") +
        (busy ? " opacity-60 cursor-wait" : "")
      }
      title={checked ? labelOn : labelOff}
    >
      <span
        className={
          "inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform " +
          (checked ? "translate-x-4" : "translate-x-0.5")
        }
      />
      <span className="sr-only">{checked ? labelOn : labelOff}</span>
    </button>
  );
}

/* --------------------------------------------------------------------- *
 * AdminDataGrid
 * --------------------------------------------------------------------- */

/**
 * Props:
 *   columns: [{
 *     key: string                          // unique field key (used for sort)
 *     header: string                       // column title
 *     sortable?: boolean                   // default false
 *     align?: "left"|"right"|"center"      // default "left"
 *     width?: string                       // CSS width hint (e.g. "120px")
 *     render: (row, helpers) => ReactNode  // cell renderer
 *   }]
 *   rows: object[]
 *   getRowId: (row) => string              // stable key for React
 *
 *   // Sort (controlled)
 *   sortBy: string | null
 *   sortDir: "asc" | "desc"
 *   onSortChange: ({sortBy, sortDir}) => void
 *
 *   // Pagination (controlled, server-driven by default)
 *   page: number                           // 1-indexed
 *   pageSize: number
 *   totalCount: number
 *   onPageChange: (page) => void
 *
 *   // Search (controlled)
 *   search?: string
 *   onSearchChange?: (s) => void
 *   searchPlaceholder?: string
 *
 *   // Filters (controlled)
 *   filters?: [{value, label}]
 *   activeFilter?: string
 *   onFilterChange?: (v) => void
 *
 *   // States
 *   loading?: boolean
 *   emptyState?: ReactNode                 // shown when totalCount === 0
 *   filterEmptyState?: ReactNode           // shown when filters narrow to 0
 *
 *   // Misc
 *   testId?: string                        // root data-testid prefix
 *   className?: string                     // wrapper override
 *   tableRowClassName?: (row) => string    // per-row classes (e.g. opacity-60 for inactive)
 */
export default function AdminDataGrid({
  columns, rows, getRowId,
  sortBy, sortDir, onSortChange,
  page, pageSize, totalCount, onPageChange,
  search, onSearchChange, searchPlaceholder = "Search…",
  filters, activeFilter, onFilterChange,
  loading,
  emptyState,
  filterEmptyState,
  testId = "admin-data-grid",
  className = "",
  tableRowClassName,
}) {
  const totalPages = Math.max(1, Math.ceil((totalCount || 0) / pageSize));
  const safePage = Math.min(Math.max(1, page || 1), totalPages);
  const from = totalCount === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const to = Math.min(safePage * pageSize, totalCount || 0);

  const handleHeaderClick = (col) => {
    if (!col.sortable || !onSortChange) return;
    if (sortBy === col.key) {
      onSortChange({ sortBy: col.key, sortDir: sortDir === "asc" ? "desc" : "asc" });
    } else {
      onSortChange({ sortBy: col.key, sortDir: "asc" });
    }
  };

  const sortIcon = (col) => {
    if (!col.sortable) return null;
    if (sortBy !== col.key) {
      return <ChevronsUpDown className="w-3 h-3 inline-block ml-1 text-slate-400" aria-hidden="true" />;
    }
    return sortDir === "asc"
      ? <ChevronUp className="w-3 h-3 inline-block ml-1 text-slate-700" aria-hidden="true" />
      : <ChevronDown className="w-3 h-3 inline-block ml-1 text-slate-700" aria-hidden="true" />;
  };

  return (
    <div
      data-testid={testId}
      className={"bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden " + className}
    >
      {/* Toolbar — search + filters + total count */}
      {(onSearchChange || filters) && (
        <div
          className="flex items-center gap-2 flex-wrap px-4 py-3 border-b border-slate-200 bg-slate-50"
          data-testid={`${testId}-toolbar`}
        >
          {onSearchChange && (
            <label className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" aria-hidden="true" />
              <input
                data-testid={`${testId}-search`}
                type="search"
                value={search || ""}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full h-8 rounded-md border border-slate-200 pl-7 pr-2 text-xs focus:outline-none focus:ring-1 focus:ring-orange-400 bg-white"
              />
            </label>
          )}
          {filters && filters.length > 0 && (
            <div className="flex items-center gap-1 text-xs" data-testid={`${testId}-filters`}>
              {filters.map((f) => (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => onFilterChange?.(f.value)}
                  data-testid={`${testId}-filter-${f.value}`}
                  className={
                    "px-2.5 py-1 rounded-md border transition " +
                    (activeFilter === f.value
                      ? "bg-slate-900 text-white border-slate-900"
                      : "bg-white text-slate-600 border-slate-200 hover:border-slate-300")
                  }
                >
                  {f.label}
                </button>
              ))}
            </div>
          )}
          <div className="text-[11px] text-slate-500 ml-auto" data-testid={`${testId}-toolbar-count`}>
            {totalCount === 0 ? "0 items" : `${from}–${to} of ${totalCount}`}
          </div>
        </div>
      )}

      {/* Body */}
      {loading ? (
        <div className="px-4 py-10 text-center text-sm text-slate-500" data-testid={`${testId}-loading`}>
          Loading…
        </div>
      ) : totalCount === 0 ? (
        <div className="px-4 py-10 text-center text-sm text-slate-500" data-testid={`${testId}-empty`}>
          {emptyState || "No items yet."}
        </div>
      ) : rows.length === 0 ? (
        <div className="px-4 py-10 text-center text-sm text-slate-500" data-testid={`${testId}-filter-empty`}>
          {filterEmptyState || "No items match your filters."}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm border-collapse" data-testid={`${testId}-table`}>
            <thead>
              <tr className="bg-slate-50 text-xs text-slate-600 uppercase tracking-wide">
                {columns.map((col, idx) => (
                  <th
                    key={col.key}
                    data-testid={`${testId}-header-${col.key}`}
                    data-sortable={col.sortable ? "true" : "false"}
                    onClick={() => handleHeaderClick(col)}
                    className={
                      "px-3 py-2 font-medium border-b border-r border-slate-200 last:border-r-0 " +
                      "select-none " +
                      (col.sortable ? "cursor-pointer hover:bg-slate-100 " : "") +
                      (col.align === "right" ? "text-right "
                        : col.align === "center" ? "text-center " : "text-left ")
                    }
                    style={col.width ? { width: col.width } : undefined}
                  >
                    <span>{col.header}</span>
                    {sortIcon(col)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const rowId = getRowId(row);
                const extra = tableRowClassName ? tableRowClassName(row) : "";
                return (
                  <tr
                    key={rowId}
                    data-testid={`${testId}-row-${rowId}`}
                    className={
                      "border-b border-slate-100 last:border-b-0 hover:bg-slate-50/60 " + extra
                    }
                  >
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        data-testid={`${testId}-cell-${rowId}-${col.key}`}
                        className={
                          "px-3 py-2 border-r border-slate-100 last:border-r-0 align-middle " +
                          (col.align === "right" ? "text-right "
                            : col.align === "center" ? "text-center " : "text-left ")
                        }
                      >
                        {col.render(row)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination footer */}
      {totalCount > pageSize && (
        <div
          className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50 text-xs"
          data-testid={`${testId}-pagination`}
        >
          <div className="text-slate-600">
            Page <strong>{safePage}</strong> of <strong>{totalPages}</strong>
            <span className="ml-2 text-slate-400">· {totalCount} total</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => onPageChange?.(1)}
              disabled={safePage <= 1}
              data-testid={`${testId}-page-first`}
              className="px-2 py-1 rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              ≪
            </button>
            <button
              type="button"
              onClick={() => onPageChange?.(safePage - 1)}
              disabled={safePage <= 1}
              data-testid={`${testId}-page-prev`}
              className="px-2 py-1 rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              ← Prev
            </button>
            <button
              type="button"
              onClick={() => onPageChange?.(safePage + 1)}
              disabled={safePage >= totalPages}
              data-testid={`${testId}-page-next`}
              className="px-2 py-1 rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next →
            </button>
            <button
              type="button"
              onClick={() => onPageChange?.(totalPages)}
              disabled={safePage >= totalPages}
              data-testid={`${testId}-page-last`}
              className="px-2 py-1 rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              ≫
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
